// Column definitions for the three Relationship tables, matching the mock:
// Across-Document Citations, Within-Document References, Semantic Relationships.
import { ArrowLeftRight, ArrowRight } from "lucide-react";
import type { Column } from "../../../../components/DataTable";
import { ConfidenceBar, StatusTag, TypeTag } from "../parts";
import type { CitationRow, InternalRefRow, SemanticRow } from "./types";
import { REL_COLOR, RELATIONSHIP_COPY } from "./constants";
import { typeHex } from "./helpers";

const EVIDENCE_CLS =
  "block max-w-[320px] text-xs leading-[1.4] text-muted-foreground break-words [word-break:normal]";
const QUOTE_CLS = "font-medium not-italic text-foreground";
const MUTED_DASH = <span className="text-muted-foreground">-</span>;

export const citationColumns: Column<CitationRow>[] = [
  {
    key: "status",
    header: "Status",
    maxChars: false,
    align: "left",
    width: "120px",
    render: (row) => <StatusTag status={row.status} />,
  },
  {
    key: "type",
    header: "Relationship",
    maxChars: false,
    align: "left",
    width: "160px",
    render: (row) => <TypeTag type={row.type} />,
  },
  {
    key: "evidence",
    header: "Evidence",
    maxChars: false,
    align: "left",
    minWidth: 240,
    tooltip: (row) => row.reason || row.evidence,
    render: (row) => (
      <span className={EVIDENCE_CLS}>
        <span className={QUOTE_CLS}>{row.evidence || "-"}</span>
      </span>
    ),
  },
  {
    key: "target",
    header: "Target",
    maxChars: false,
    align: "left",
    minWidth: 180,
    render: (row) => (
      <span className="text-xs font-medium text-foreground break-words [word-break:normal]">
        {row.target}
      </span>
    ),
  },
  {
    key: "section",
    header: "Section",
    maxChars: false,
    align: "left",
    width: "130px",
    render: (row) => <span className="whitespace-nowrap text-xs text-foreground">{row.section || "-"}</span>,
  },
  {
    key: "method",
    header: "Method",
    maxChars: false,
    align: "center",
    width: "80px",
    render: (row) => (
      <span className="text-[10px] text-muted-foreground">{row.method || "LLM"}</span>
    ),
  },
  {
    key: "confidence",
    header: "Confidence",
    maxChars: false,
    align: "left",
    width: "110px",
    render: (row) => <ConfidenceBar value={row.confidence} />,
  },
];

export const internalRefColumns: Column<InternalRefRow>[] = [
  {
    key: "status",
    header: "Status",
    maxChars: false,
    align: "left",
    width: "120px",
    render: (row) => <StatusTag status={row.status} />,
  },
  {
    key: "type",
    header: "Type",
    maxChars: false,
    align: "left",
    width: "120px",
    render: (row) => <TypeTag type={row.type} />,
  },
  {
    key: "evidence",
    header: "Evidence",
    maxChars: false,
    align: "left",
    minWidth: 260,
    render: (row) => (
      <span className={EVIDENCE_CLS}>
        <span className={QUOTE_CLS}>{row.evidence || "-"}</span>
        {row.reason && (
          <span className="mt-[3px] block text-[10px] text-muted-foreground/80">{row.reason}</span>
        )}
      </span>
    ),
  },
  {
    key: "target",
    header: "Target",
    maxChars: false,
    align: "left",
    width: "110px",
    render: (row) => <span className="text-xs font-medium text-foreground">{row.target}</span>,
  },
  {
    key: "section",
    header: "In section",
    maxChars: false,
    align: "left",
    width: "100px",
    render: (row) => <span className="text-xs text-foreground">{row.section || "-"}</span>,
  },
  {
    key: "method",
    header: "Method",
    maxChars: false,
    align: "center",
    width: "80px",
    render: (row) =>
      row.method ? <span className="text-[10px] text-muted-foreground">{row.method}</span> : MUTED_DASH,
  },
  {
    key: "confidence",
    header: "Confidence",
    maxChars: false,
    align: "left",
    width: "110px",
    render: (row) => <ConfidenceBar value={row.confidence} />,
  },
];

export const semanticColumns: Column<SemanticRow>[] = [
  {
    key: "rel",
    header: "Relationship",
    maxChars: false,
    align: "left",
    width: "170px",
    render: (row) => (
      <div className="flex flex-col items-start">
        <TypeTag type={row.rel} strong />
        {row.symmetric && (
          <span className="mt-[3px] text-[9px] text-muted-foreground">{RELATIONSHIP_COPY.mutual}</span>
        )}
      </div>
    ),
  },
  {
    key: "src",
    header: "Source clause",
    maxChars: false,
    align: "left",
    width: "100px",
    render: (row) => <span className="text-[11px] font-medium text-foreground">{row.src}</span>,
  },
  {
    key: "tgt",
    header: "Target clause",
    maxChars: false,
    align: "left",
    width: "100px",
    render: (row) => <span className="text-[11px] font-medium text-foreground">{row.tgt}</span>,
  },
  {
    key: "evidence",
    header: "Evidence, both sides",
    maxChars: false,
    align: "left",
    minWidth: 340,
    render: (row) => {
      const color = typeHex(row.rel);
      const Arrow = row.symmetric ? ArrowLeftRight : ArrowRight;
      return (
        <div className="max-w-[460px] text-xs leading-[1.4] text-muted-foreground [word-break:normal]">
          <div className="mb-[5px] border-l-2 pl-[7px]" style={{ borderLeftColor: color }}>
            <span className={QUOTE_CLS}>{row.srcEvidence || "-"}</span>
          </div>
          <Arrow className="mb-[3px] size-3 text-muted-foreground" />
          <div className="border-l-2 border-border pl-[7px]">
            <span className={QUOTE_CLS}>{row.tgtEvidence || "-"}</span>
          </div>
          {row.why && <div className="mt-1.5 text-[10px] italic text-muted-foreground">{row.why}</div>}
        </div>
      );
    },
  },
  {
    key: "similarity",
    header: "Similarity",
    maxChars: false,
    align: "left",
    width: "110px",
    render: (row) =>
      row.similarity === null ? MUTED_DASH : <ConfidenceBar value={row.similarity} color={REL_COLOR.neutral} />,
  },
  {
    key: "confidence",
    header: "Confidence",
    maxChars: false,
    align: "left",
    width: "110px",
    render: (row) => <ConfidenceBar value={row.confidence} />,
  },
];
